import { Typography } from '@mui/material';
import { Button } from '@components/button';
import { NavigateFunction } from 'react-router-dom';
import { PolicyGetResponse } from '@lio/api-types';
import { PolicyRelationType, PolicyStatus } from '@lio/rule-engine';
import { PolicyDialogType, EndorsementPolicyDialogType, RenewDialogType } from '@models/policyTransaction';

import { getFormattedPolicyNumber } from '@utils/policy/getFormattedPolicyNumber';

export const checkIfTransactionCreateIsValid = ({
  policy,
  dialogType,
  onCancel,
  navigate,
}: {
  onCancel: () => void;
  policy: PolicyGetResponse;
  dialogType: PolicyDialogType | EndorsementPolicyDialogType | RenewDialogType;
  navigate: NavigateFunction;
}) => {
  const formattedPolicyNumber =
    policy?.policyNumberPrefix &&
    getFormattedPolicyNumber(policy?.policyNumberPrefix, policy?.policyNumber.policyNumber);

  switch (dialogType) {
    case PolicyDialogType.Reinstatement: {
      const policySourceUnderlyingCoverage = policy?.relatedPolicySources?.find((s) => {
        return (
          s.policyRelationType === PolicyRelationType.UnderlyingCoverage &&
          // A reinstated past-expiry policy lands in Expired, which still satisfies the underlying coverage requirement
          s.sourcePolicy.status !== PolicyStatus.Active &&
          s.sourcePolicy.status !== PolicyStatus.Expired
        );
      });

      if (!policySourceUnderlyingCoverage) {
        return null;
      }
      return {
        title: `Cannot Reinstate ${formattedPolicyNumber}`,
        DialogContent: (
          <Typography variant='bodySmall'>
            This policy cannot be reinstated, as{' '}
            <Button
              variant='text'
              onClick={() => {
                onCancel();
                navigate(`/policy/${policySourceUnderlyingCoverage.sourcePolicy.id}`);
              }}
            >
              {getFormattedPolicyNumber(
                policySourceUnderlyingCoverage.sourcePolicy.policyNumberPrefix,
                policySourceUnderlyingCoverage.sourcePolicy.policyNumber.policyNumber,
              )}
            </Button>{' '}
            provides the underlying coverage and must be reinstated first.
          </Typography>
        ),
      };
    }
    case PolicyDialogType.Cancelation: {
      const policyDestinationUnderlyingCoverage = policy?.relatedPolicyDestinations?.find((d) => {
        return (
          d.policyRelationType === PolicyRelationType.UnderlyingCoverage &&
          d.destinationPolicy.status !== PolicyStatus.Canceled
        );
      });

      if (!policyDestinationUnderlyingCoverage) {
        return null;
      }

      return {
        title: `Cannot Cancel ${formattedPolicyNumber}`,
        DialogContent: (
          <Typography variant='bodySmall'>
            This policy cannot be cancelled, as it provides underlying coverage for{' '}
            <Button
              variant='text'
              onClick={() => {
                onCancel();
                navigate(`/policy/${policyDestinationUnderlyingCoverage?.destinationPolicy.id}`);
              }}
            >
              {getFormattedPolicyNumber(
                policyDestinationUnderlyingCoverage.destinationPolicy.policyNumberPrefix,
                policyDestinationUnderlyingCoverage.destinationPolicy.policyNumber.policyNumber,
              )}
            </Button>{' '}
            which must be cancelled first.
          </Typography>
        ),
      };
    }

    default:
      return null;
  }
};
